import React, { useState, useRef, useEffect } from "react";
import logo from "../../assets/img/prestation/logo-azurea.png";
import "./prestation.css";
import folder from "../../assets/img/folderContainer.svg";
import {
  FaWifi,
  FaPhone,
  FaPrint,
  FaCloud,
  FaInbox,
  FaChevronUp,
  FaArrowRight,
  FaMousePointer,
} from "react-icons/fa";
import { FaWindowRestore } from "react-icons/fa6";
import { GoArrowRight } from "react-icons/go";

/* ---------- Section unitaire ---------- */
function PrestationComponent({
  number,
  titleDesktop,
  titleMobile,
  subtitleDesktop,
  paragraph,
  IconComponent,
  forceOpen,
}) {
  const [active, setActive] = useState(false);
  const contentRef = useRef(null);

  // Suivre la demande du parent (ouverture ciblée)
  useEffect(() => {
    setActive(!!forceOpen);
  }, [forceOpen]);

  // Animation hauteur
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (active) {
      const h = el.scrollHeight;
      el.style.height = h + "px";
    } else {
      el.style.height = el.scrollHeight + "px";
      requestAnimationFrame(() => {
        el.style.height = "0px";
      });
    }
  }, [active]);

  return (
    <section
      className={`section ${active ? "active" : ""}`}
      id={`prestation-${number}`}
    >
      <div
        className="section-header"
        onClick={() => setActive((prev) => !prev)}
        style={{ cursor: "pointer" }}
      >
        <div className="section-left">
          <span className="section-number">{number}</span>
          <FaChevronUp
            className={`icon-rotate ${active ? "rotate-180" : ""}`}
          />
        </div>

        <div className="section-right">
          <h2 className="section-title">
            <span className="desktop-text">{titleDesktop}</span>
            <span className="mobile-text">{titleMobile}</span>
          </h2>

          <p className="short-text">
            <span className="desktop-text">{subtitleDesktop}</span>
            <span className="mobile-text">&nbsp;</span>
          </p>

          <div className="icon-border">
            <IconComponent className="icon" />
          </div>

          <div
            className="section-content"
            ref={contentRef}
            style={{
              height: "0px",
              overflow: "hidden",
              transition: "height 0.3s ease",
            }}
          >
            {paragraph}
            <img
              className="section-img"
              src={require(`../../assets/img/prestation/section-img-${number}.jpg`)}
              alt={`Section ${number}`}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Petite carte ---------- */
function Card(number, title, color) {
  return (
    <div className="container-cards">
      <div className={`head-part ${color}-card`}>
        <h2 className="card-number">{number}</h2>
        <h3 className="card-title">{title}</h3>
      </div>
      <div className={`footer-part ${color}-card`}>
        <p className="card-contact">Contactez-nous</p>
      </div>
      <div className={`icon-part ${color}-card`}>
        <div className="icon-part-inner">
          <a className="icon-part-link" href="#contact">
            <GoArrowRight style={{ fill: "#0a2c61" }} />
          </a>
        </div>
      </div>
    </div>
  );
}

/* ---------- Liste des prestations + liaison clic service ---------- */
export default function Prestations() {
  const [allOpen, setAllOpen] = useState(false);
  const [openedId, setOpenedId] = useState(null); // "01".."07"

  // Écoute l’évènement envoyé par App.js
  useEffect(() => {
    const onOpen = (e) => {
      const id = e?.detail?.id ?? null; // ex: "03"
      setOpenedId(id);
    };
    window.addEventListener("open-prestation", onOpen);
    return () => window.removeEventListener("open-prestation", onOpen);
  }, []);

  return (
    <div className="containere prestation" id="prestations">
      <img className="logo-fond" src={logo} alt="Logo Azurea" />

      <div className="prestation-loc-container">
        <div className="prestation-loc">ALPES-MARITIMES (06) & VAR (83)</div>
      </div>

      <h2 className="prestation-title">
        AZUREA PRINT ET SOLUTIONS : <br />
        <span>Solutions IT & télécoms</span> pour entreprises
      </h2>

      <div className="all-prestations">
        <PrestationComponent
          forceOpen={allOpen || openedId === "01"}
          number="01"
          titleDesktop="Internet & Réseaux"
          titleMobile="Internet & Réseaux"
          subtitleDesktop="Connexion Haut Débit Sécurisée pour les Entreprises"
          paragraph={
            <>
              <p>
                Des solutions réseau fiables et performantes
                <br />
                Optez pour une connexion Internet ultra-performante et un réseau
                sécurisé, adaptés aux besoins des PME et grandes entreprises.
              </p>
              <ul>
                <li>Fibre optique et ADSL : Débit rapide, latence réduite</li>
                <li>Sécurisation et mise en conformité du câblage</li>
                <li>
                  Organisation de baie de brassage pour une infrastructure
                  optimisée
                </li>
                <li>Réseau professionnel stable et résilient</li>
              </ul>
              <p>
                📍 Installation et maintenance dans les Alpes-Maritimes (06) et
                le Var (83).
                <br />
                🔍 Optimisez la connectivité de votre entreprise dès maintenant.
              </p>
            </>
          }
          IconComponent={FaWifi}
        />

        <PrestationComponent
          forceOpen={allOpen || openedId === "02"}
          number="02"
          titleDesktop="Téléphonie VoIP & Solutions de Communication d’Entreprise"
          titleMobile="Téléphonie VoIP"
          subtitleDesktop="Une Communication Impactante et Connectée"
          paragraph={
            <>
              <p>
                Profitez d’une téléphonie IP fiable et évolutive pour une
                gestion fluide de vos appels.
              </p>
              <ul>
                <li>
                  Téléphonie IP & Softphone : Accédez à vos appels depuis
                  n’importe où
                </li>
                <li>
                  Standard téléphonique d’entreprise : Répondeurs, files
                  d’attente, pré-décroché
                </li>
                <li>
                  Intégration multi-sites : Communication centralisée entre vos
                  bureaux
                </li>
                <li>
                  Réduction des coûts : Diminuez vos factures télécoms sans
                  compromis sur la qualité
                </li>
                <li>
                  Boostez la productivité et fluidifiez votre communication
                  interne.
                </li>
                <li>Téléphonie mobile professionnelle</li>
              </ul>
            </>
          }
          IconComponent={FaPhone}
        />

        <PrestationComponent
          forceOpen={allOpen || openedId === "03"}
          number="03"
          titleDesktop="Solutions d’Impression & Numérisation Professionnelles"
          titleMobile="Impression & Scan"
          subtitleDesktop="Optimisez la gestion de vos documents et vos impressions"
          paragraph={
            <>
              <p>
                Gagnez en productivité avec des équipements adaptés aux besoins
                des PME et grandes entreprises.
              </p>
              <ul>
                <li>
                  Imprimantes sécurisées haute qualité : Impression multi-format
                </li>
                <li>Gestion des coûts & contrôle des impressions</li>
                <li>Numérisation OCR avancée & archivage intelligent</li>
                <li>Copieur multifonction</li>
                <li>Impression et numérisation en haute qualité</li>
                <li>Traceur grand format</li>
              </ul>
              <p>
                📍 Solutions adaptées aux entreprises en 06 et 83.
                <br />
                🖥️ Passez à la gestion documentaire digitale et sécurisée.
              </p>
            </>
          }
          IconComponent={FaPrint}
        />

        <PrestationComponent
          forceOpen={allOpen || openedId === "04"}
          number="04"
          titleDesktop="Dématérialisation & Gestion Électronique des Documents (GED)"
          titleMobile="Dématérialisation"
          subtitleDesktop="Simplifiez et sécurisez votre transition numérique"
          paragraph={
            <>
              <p>
                Passez à la gestion 100% digitale avec des outils performants et
                conformes aux normes.
              </p>
              <ul>
                <li>Dématérialisation des factures et courriers</li>
                <li>
                  Archivage et recherche rapide grâce à l’intelligence
                  documentaire
                </li>
                <li>Signature électronique conforme eIDAS</li>
                <li>
                  Optimisez vos flux documentaires et gagnez en efficacité.
                </li>
              </ul>
            </>
          }
          IconComponent={FaCloud}
        />

        <PrestationComponent
          forceOpen={allOpen || openedId === "05"}
          number="05"
          titleDesktop="Écrans Multifonctions Interactifs"
          titleMobile="Écrans Multifonctions Interactifs"
          subtitleDesktop="Collaboration & Innovation en Entreprise"
          paragraph={
            <>
              <p>
                Un outil puissant pour vos réunions et formations
                <br />
                Améliorez l’interactivité et la productivité avec nos écrans
                tactiles professionnels.
              </p>
              <ul>
                <li>Écran multifonction interactif</li>
                <li>Vidéo projection</li>
                <li>Tableau blanc interactif & annotations en direct</li>
                <li>Mode projection et affichage dynamique</li>
                <li>Écran 4K</li>
                <li>Ordinateur embarqué</li>
              </ul>
              <p>📍 Installation sur site en Alpes-Maritimes et Var.</p>
            </>
          }
          IconComponent={FaMousePointer}
        />

        <PrestationComponent
          forceOpen={allOpen || openedId === "06"}
          number="06"
          titleDesktop="Affichage Dynamique"
          titleMobile="Affichage Dynamique"
          subtitleDesktop="Une Communication Impactante et Connectée"
          paragraph={
            <>
              <p>
                Diffusion en temps réel sur vos écrans d’entreprise
                <br />
                Renforcez votre image et engagez vos clients avec des solutions
                d’affichage digital performantes.
              </p>
              <ul>
                <li>Écrans interactifs, totems et pupitres</li>
                <li>Mise à jour en temps réel & gestion à distance</li>
                <li>Affichage corporate, marketing ou interne optimisé</li>
              </ul>
              <p>
                📍 Installation sur site en Alpes-Maritimes et Var.
                <br />
                🎯 Maximisez l’impact de vos messages avec une communication
                visuelle puissante.
              </p>
            </>
          }
          IconComponent={FaWindowRestore}
        />

        <PrestationComponent
          forceOpen={allOpen || openedId === "07"}
          number="07"
          titleDesktop="Sécurité & Sauvegarde Informatique"
          titleMobile="Sécurité IT"
          subtitleDesktop="Protégez Vos Données"
          paragraph={
            <>
              <p>
                Des solutions avancées contre la perte de données et les
                cyberattaques
                <br />
                La sécurisation des données est essentielle pour garantir la
                continuité de votre activité.
              </p>
              <ul>
                <li>
                  Sauvegarde externalisée & stockage sécurisé (boîtier anti-feu,
                  anti-inondation)
                </li>
                <li>Protection contre les cyberattaques et conformité ANSSI</li>
                <li>
                  Plan de reprise d’activité (PRA) pour une résilience totale
                </li>
              </ul>
              <p>Effectuez un audit gratuit de votre système de sauvegarde.</p>
            </>
          }
          IconComponent={FaInbox}
        />
      </div>

      <button
        id="toggleSectionsBtn"
        className="show-more-button"
        onClick={() => setAllOpen((prev) => !prev)}
      >
        {allOpen ? "Tout réduire" : "Tout déplier"}{" "}
        <FaArrowRight
          className={`fa-rotate-180 ${allOpen ? "rotate-90" : ""}`}
        />
      </button>

      <section className="whyAsking">
        <img className="wa-img" src={folder} alt="" />
        <div className="folderContainer">
          <div className="center">
            <div className="wa-tag-container">
              <div className="wa-tag">EXPERTISE, RÉACTIVITÉ, SÉCURITÉ</div>
            </div>
            <h1 className="wa-title">
              Pourquoi <br />
              Nous <span>Choisir</span> ?
            </h1>
            <div className="card-container">
              <div className="blank"></div>
              {Card(
                "01",
                "Expertise en IT et Télécom dédiée aux entreprises",
                "grey"
              )}
              {Card(
                "02",
                "Solutions sur-mesure adaptées à vos besoins spécifiques",
                "blue"
              )}
              {Card(
                "03",
                "Intervention rapide et support technique local (06 & 83)",
                "grey"
              )}
              {Card(
                "04",
                "Technologies innovantes et conformes aux normes de sécurité",
                "blue"
              )}
              {Card(
                "05",
                "Interlocuteur commercial & technique privilégié",
                "grey"
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
